import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import debounce from 'lodash-es/debounce';
import { XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import { BlockNoteEditorComponent } from '@/components/atoms/Editors/BlockNoteEditor/BlockNoteEditor';
import { getGuidelinesEditorOverrides } from '@/components/atoms/Editors/BlockNoteEditor/config';
import { Button } from '@/components/ui/button';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { Separator } from '@/components/ui/separator';
import useBrains from '@/hooks/useBrains';
import { useBrainVariables } from '@/hooks/useBrainVariables';
import useDialogs from '@/hooks/useDialogs';
import { useEffectiveTools } from '@/hooks/useEffectiveTools';
import { selectAccountSlug, selectBrainId } from '@/redux/session/selectors';

const CLOSE_ANIMATION_MS = 300;

type EditorApi = { appendContent: (markdown: string) => void; getMarkdown: () => Promise<string> };

interface AIResponseGuidelinesPanelProps {
  guidelines: string;
  onChange: (guidelines: string) => void;
  onClose: () => void;
}

/**
 * Right-side drawer for editing an AI Response step's guideline. Mirrors the
 * Authentication guidelines drawer (`AuthPageGuidelinesPanel`) — a wide drawer
 * with a left gutter that gives the shared `BlockNoteEditorComponent` room for
 * its side-menu/formatting chrome (which overflows a narrow toolkit panel).
 *
 * Unlike auth, it passes `enabledTools`/`dialogs` so the editor offers `@`
 * local-tool autocomplete (incl. `trigger_workflow`) alongside `$` variables
 * and `/` markdown — the AI Response synthesis runs with the brain's tools.
 */
const AIResponseGuidelinesPanel = ({
  guidelines,
  onChange,
  onClose,
}: AIResponseGuidelinesPanelProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const brainId = useSelector(selectBrainId);
  const slug = useSelector(selectAccountSlug);
  const { brain } = useBrains(brainId);
  const { dialogs } = useDialogs(brainId);
  const { contextVariables } = useBrainVariables();
  const { effectiveTools: enabledTools } = useEffectiveTools(brain);

  const dictionary = useMemo(
    () =>
      getGuidelinesEditorOverrides({
        defaultPlaceholder: t('dialog.ai_response.guidelines_placeholder'),
      }).dictionary,
    [t]
  );

  const [isOpen, setIsOpen] = useState(true);
  const getMarkdownRef = useRef<(() => Promise<string>) | null>(null);
  const closingRef = useRef(false);

  const persistGuideline = useMemo(
    () =>
      debounce(async () => {
        const next = (await getMarkdownRef.current?.()) ?? guidelines;
        onChange(next);
      }, 500),
    [onChange, guidelines]
  );

  useEffect(() => () => persistGuideline.cancel(), [persistGuideline]);

  const handleEditorReady = useCallback((editorApi: EditorApi) => {
    getMarkdownRef.current = editorApi.getMarkdown;
  }, []);

  const handleChange = useCallback(() => {
    persistGuideline();
  }, [persistGuideline]);

  const onNavigateToLocalTools = useCallback(() => {
    navigate(`/${slug}/ai-agents/${brainId}/mcps?tab=local`);
  }, [navigate, slug, brainId]);

  const handleClose = useCallback(() => {
    if (closingRef.current) {
      return;
    }
    closingRef.current = true;
    persistGuideline.flush();
    setIsOpen(false);
    setTimeout(() => {
      onClose();
    }, CLOSE_ANIMATION_MS);
  }, [onClose, persistGuideline]);

  return (
    <Drawer
      direction="right"
      open={isOpen}
      onOpenChange={() => {}}
      modal={false}
      shouldScaleBackground={false}
      dismissible={false}
    >
      <DrawerContent className="top-[calc(var(--newheader-height)+var(--subheader-height))] bottom-0 h-auto w-[768px] border-l shadow-lg">
        <div className="relative">
          <Button
            variant="ghost-neutral"
            size="icon-sm"
            aria-label={t('common.close')}
            onClick={handleClose}
            className="absolute top-4 right-4 z-10"
          >
            <XIcon size={16} />
          </Button>
        </div>

        <DrawerHeader className="gap-0 pt-11 pr-12 pb-0 pl-24">
          <DrawerTitle className="text-lg font-semibold text-foreground">
            {t('dialog.ai_response.guidelines_editor_title')}
          </DrawerTitle>
          <DrawerDescription className="mt-1 text-sm text-muted-foreground">
            {t('dialog.ai_response.guidelines_editor_description')}
          </DrawerDescription>
        </DrawerHeader>

        <Separator className="mt-4 mr-6 ml-24 w-auto" />

        <div className="flex-1 overflow-y-auto pt-4 pr-6 pb-6 pl-24" data-vaul-no-drag>
          <BlockNoteEditorComponent
            initialMarkdown={guidelines}
            dictionary={dictionary}
            contextVariableOptions={contextVariables}
            enabledTools={enabledTools}
            dialogs={dialogs}
            onNavigateToLocalTools={onNavigateToLocalTools}
            onEditorReady={handleEditorReady}
            onChange={handleChange}
          />
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default AIResponseGuidelinesPanel;
